import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Download, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

const ExcelImportDialog = ({ isOpen, onClose, onImportComplete }) => {
  const [step, setStep] = useState(1); // 1 = upload, 2 = preview, 3 = importing
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [editedProducts, setEditedProducts] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const isValidExtension = selectedFile.name.match(/\.(xlsx|xls|csv)$/i);
    
    if (!isValidExtension) {
      toast.error('Bitte wählen Sie eine Excel (.xlsx, .xls) oder CSV-Datei');
      return;
    }

    setFile(selectedFile);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/import/products/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPreviewData(response.data);
      setEditedProducts(response.data.products.map(p => ({ ...p })));
      setStep(2);
      toast.success(`${response.data.valid_products} Produkte erkannt`);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Lesen der Datei');
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/import/products/template', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Produkt_Import_Vorlage.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Vorlage heruntergeladen');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Fehler beim Download der Vorlage');
    }
  };

  const updateProduct = (index, field, value) => {
    setEditedProducts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Re-validate
      const product = updated[index];
      product.errors = [];
      product.valid = true;
      
      if (!product.name?.trim()) {
        product.valid = false;
        product.errors.push('Name fehlt');
      }
      if (!product.price || product.price <= 0) {
        product.valid = false;
        product.errors.push('Preis fehlt');
      }
      
      return updated;
    });
  };

  const removeProduct = (index) => {
    setEditedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleImport = async () => {
    const validProducts = editedProducts.filter(p => p.valid && p.name?.trim() && p.price > 0);
    
    if (validProducts.length === 0) {
      toast.error('Keine gültigen Produkte zum Importieren');
      return;
    }

    setIsImporting(true);
    setStep(3);

    try {
      const response = await axiosInstance.post('/import/products/execute', validProducts);

      toast.success(response.data.message);
      
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} Produkte konnten nicht importiert werden`);
      }

      onImportComplete?.();
      handleClose();

    } catch (error) {
      console.error('Import error:', error);
      toast.error('Fehler beim Importieren');
      setStep(2);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setEditedProducts([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const validCount = editedProducts.filter(p => p.valid).length;
  const invalidCount = editedProducts.filter(p => !p.valid).length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            Excel/CSV Import
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">So funktioniert's:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Laden Sie eine Excel (.xlsx) oder CSV-Datei hoch</li>
                <li>• Die Spalten werden automatisch erkannt</li>
                <li>• Sie können die Daten vor dem Import prüfen und bearbeiten</li>
              </ul>
            </div>

            {/* Upload area */}
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {isUploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                  <p className="text-gray-600">Datei wird analysiert...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Datei hier ablegen oder klicken
                  </p>
                  <p className="text-sm text-gray-500">
                    Unterstützt: Excel (.xlsx, .xls) und CSV
                  </p>
                </>
              )}
            </div>

            {/* Download template */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Import-Vorlage herunterladen</p>
                <p className="text-sm text-gray-500">
                  Enthält die korrekten Spaltenüberschriften und Beispieldaten
                </p>
              </div>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Vorlage
              </Button>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={handleClose}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {step === 2 && previewData && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex flex-wrap gap-3">
              <Badge variant="outline" className="text-sm">
                {previewData.total_rows} Zeilen gefunden
              </Badge>
              <Badge className="bg-green-100 text-green-800 text-sm">
                {validCount} gültig
              </Badge>
              {invalidCount > 0 && (
                <Badge className="bg-red-100 text-red-800 text-sm">
                  {invalidCount} fehlerhaft
                </Badge>
              )}
            </div>

            {/* Column mapping info */}
            {previewData.detected_columns && (
              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                Erkannte Spalten: {Object.entries(previewData.detected_columns)
                  .filter(([orig, mapped]) => ['name', 'price', 'category', 'stock', 'description'].includes(mapped))
                  .map(([orig, mapped]) => `${orig} → ${mapped}`)
                  .join(', ')}
              </div>
            )}

            {/* Products list */}
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {editedProducts.map((product, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-3 ${
                    product.valid ? 'bg-white' : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Zeile {product.row}</span>
                      {product.valid ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeProduct(index)}
                      className="h-6 w-6 p-0 text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Name *</Label>
                      <Input
                        value={product.name || ''}
                        onChange={(e) => updateProduct(index, 'name', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Preis (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={product.price || ''}
                        onChange={(e) => updateProduct(index, 'price', parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Kategorie</Label>
                      <select
                        value={product.category || 'Sonstiges'}
                        onChange={(e) => updateProduct(index, 'category', e.target.value)}
                        className="w-full h-8 px-2 rounded-md border border-gray-300 text-sm"
                      >
                        {previewData.available_categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        <option value="Sonstiges">Sonstiges</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Lagerbestand</Label>
                      <Input
                        type="number"
                        value={product.stock || 10}
                        onChange={(e) => updateProduct(index, 'stock', parseInt(e.target.value) || 0)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>

                  {product.errors?.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {product.errors.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {previewData.has_more && (
              <p className="text-sm text-orange-600 text-center">
                Nur die ersten 100 Produkte werden in der Vorschau angezeigt. 
                Alle {previewData.total_rows} werden importiert.
              </p>
            )}

            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(1)}>
                ← Andere Datei
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleImport}
                  disabled={validCount === 0}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {validCount} Produkt(e) importieren
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
            <p className="text-lg font-medium">Importiere Produkte...</p>
            <p className="text-sm text-gray-500">Bitte warten Sie einen Moment</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExcelImportDialog;
